"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { FaSearch, FaPlus, FaEdit, FaProjectDiagram, FaFileExcel, FaFilter, FaChartBar, FaTrash, FaSort, FaSortUp, FaSortDown } from "react-icons/fa"
import { Pagination } from "../../utils/pagination.utils"
import { ExcelDownload } from "../../utils/excel.utils"
import { useAuth } from "../../provider/auth.provider"
import axiosInstance from "../../libs/axios"
import { useDebouncedCallback } from "../../hooks/useDebounce"

interface Project {
  id: number
  project_name: string
  status: string
  created_at: string
}

interface Activity {
  id: number
  activity_name: string
  activity_description: string
  /** NULL or <= 0 means the "Stop Survey Assignment to SE" rule is off. */
  site_completion_deadline_days?: number | null
  status: "ACTIVE" | "SUSPENDED"
  created_at: string
  updated_at: string
  created_by: number
  updated_by?: number
  suspended_by?: number
  suspended_at?: string
  projects?: Project[]
  project_count?: number
}

interface ActivitiesListProps {
  activities: Activity[]
  pagination: any // Renamed Pagination to avoid redeclaration
  onSearch: (search: string) => void
  onStatusFilter: (status: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onAddActivity: () => void
  onEditActivity: (activity: Activity) => void
  onDeleteActivity: (activity: Activity) => void
  onToggleStatus: (activity: Activity) => void
  onSort?: (field: string, direction: 'ASC' | 'DESC') => void
  loading?: boolean
  searchValue?: string
  statusValue?: string
  sortState?: { field: string; direction: 'ASC' | 'DESC' }
  hideAddActivityButton?: boolean
}

export const ActivitiesList: React.FC<ActivitiesListProps> = ({
  activities,
  pagination,
  onSearch,
  onStatusFilter,
  onPageChange,
  onLimitChange,
  onAddActivity,
  onEditActivity,
  onDeleteActivity,
  onToggleStatus,
  onSort,
  loading = false,
  searchValue = "",
  statusValue = "",
  sortState,
  hideAddActivityButton = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [nameFilter, setNameFilter] = useState("")
  const [descriptionFilter, setDescriptionFilter] = useState("")
  const [projectCountFilter, setProjectCountFilter] = useState("")
  const [createdAtFilter, setCreatedAtFilter] = useState("")
  const [updatedAtFilter, setUpdatedAtFilter] = useState("")
  const [showExcelModal, setShowExcelModal] = useState(false)
  const { hasPermission } = useAuth()
  const [sortConfig, setSortConfig] = useState<{
    field: string
    direction: 'ASC' | 'DESC'
  }>({
    field: 'created_at',
    direction: 'DESC'
  })
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<Record<string, any>[]>([])

  useEffect(() => {
    setSearchTerm(searchValue || "")
  }, [searchValue])

  useEffect(() => {
    setStatusFilter(statusValue || "")
  }, [statusValue])

  useEffect(() => {
    if (sortState) {
      setSortConfig(sortState)
    }
  }, [sortState])

  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedOnSearch(value)
  }

  const handleSort = (field: string) => {
    const direction =
      sortConfig.field === field && sortConfig.direction === "ASC" ? "DESC" : "ASC"
    setSortConfig({ field, direction })
    onSort?.(field, direction)
  }

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <FaSort className="w-3 h-3" />
    return sortConfig.direction === "ASC" ? <FaSortUp className="w-3 h-3" /> : <FaSortDown className="w-3 h-3" />
  }

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setStatusFilter(value)
    onStatusFilter(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Active
          </span>
        )
      case "SUSPENDED":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Suspended
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        )
    }
  }

  const getActivityIcon = (activityName: string) => {
    return <FaProjectDiagram className="w-4 h-4 text-blue-600" />
  }

  const formatDateTime = (value?: string) => (value ? new Date(value).toLocaleString() : "")

  const mapActivityForExport = (activity: Activity) => {
    const projectNames = activity.projects?.map((project) => project.project_name).filter(Boolean) || []
    const projectDetails = activity.projects?.map((project) => `${project.project_name || 'N/A'} (${project.status || 'N/A'})`).filter(Boolean) || []

    return {
      "Activity Name": activity.activity_name,
      Description: activity.activity_description || "",
      Status: activity.status,
      "Site Completion Deadline (Days)":
        activity.site_completion_deadline_days && activity.site_completion_deadline_days > 0
          ? activity.site_completion_deadline_days
          : "No deadline",
      "Projects Count": activity.project_count ?? activity.projects?.length ?? 0,
      "Project Names": projectNames.join(", "),
      "Project Details": projectDetails.join("; "),
      "Created At": formatDateTime(activity.created_at),
      "Updated At": formatDateTime(activity.updated_at),
      "Suspended At": formatDateTime(activity.suspended_at),
      // "Created By": activity.created_by,
      // "Updated By": activity.updated_by || "N/A",
      // "Suspended By": activity.suspended_by || "N/A",
    }
  }

  const buildExportParams = () => {
    const params: Record<string, any> = {}
    if (searchValue?.trim()) params.search = searchValue.trim()
    if (statusValue?.trim()) params.status = statusValue.trim()

    const activeSort = sortState || sortConfig
    if (activeSort?.field) {
      params.sort_by = activeSort.field
      params.sort_order = activeSort.direction
    }

    return params
  }

  const fetchExportData = async () => {
    try {
      setExporting(true)
      const params = buildExportParams()
      const limit = 100
      let page = 1
      let totalPages = 1
      const allActivities: Activity[] = []

      do {
        const response = await axiosInstance.get('/activities/list', {
          params: {
            ...params,
            page,
            limit,
          },
        })

        const responseData = response.data?.data
        const pageActivities: Activity[] = responseData?.activities || []
        allActivities.push(...pageActivities)

        const paginationInfo = responseData?.pagination
        if (paginationInfo) {
          totalPages = paginationInfo.total_pages ?? paginationInfo.totalPages ?? (paginationInfo.total_count ? Math.ceil(paginationInfo.total_count / limit) : totalPages)
        } else if (pageActivities.length < limit) {
          totalPages = page
        }

        page += 1
      } while (page <= totalPages)

      setExportData(allActivities.map(mapActivityForExport))
      setShowExcelModal(true)
    } catch (error) {
      console.error('Error exporting activities:', error)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Activities Management</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              <FaFileExcel className="w-4 h-4" />
              {exporting ? 'Preparing...' : 'Export Excel'}
            </button>
            {/* Hidden where the list is embedded (Activity Requirements). */}
            {!hideAddActivityButton && hasPermission("Activity", "create") && (
              <button
                onClick={onAddActivity}
                className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                <FaPlus className="w-4 h-4" />
                Add New Activity
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search Activities</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
              <input
                type="text"
                placeholder="Search by name or description"
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="w-full sm:w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Status Filter</label>
            <div className="relative">
              <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white"
              >
                <option value="">All Statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="SUSPENDED">Suspended</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                Actions
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("activity_name")}
              >
                <div className="flex items-center gap-1">
                  Activity Name
                  {getSortIcon("activity_name")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[250px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("activity_description")}
              >
                <div className="flex items-center gap-1">
                  Description
                  {getSortIcon("activity_description")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("status")}
              >
                <div className="flex items-center gap-1">
                  Status
                  {getSortIcon("status")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[160px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("site_completion_deadline_days")}
              >
                <div className="flex items-center gap-1">
                  Site Completion Deadline (Days)
                  {getSortIcon("site_completion_deadline_days")}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                <div className="space-y-2">
                  <div>Project Count</div>
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("created_at")}
              >
                <div className="flex items-center gap-1">
                  Created At
                  {getSortIcon("created_at")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("suspended_at")}
              >
                <div className="flex items-center gap-1">
                  Suspended At
                  {getSortIcon("suspended_at")}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={12} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                    <span className="text-sm text-gray-600">Loading activities...</span>
                  </div>
                </td>
              </tr>
            ) : activities.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <FaProjectDiagram className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No activities found</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your search or create a new activity</p>
                  </div>
                </td>
              </tr>
            ) : (
              activities.map((activity) => (
                <tr key={activity.id} className="hover:bg-gray-50 transition-colors">
                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {hasPermission("Activity", "edit") && (
                        <button
                          onClick={() => onEditActivity(activity)}
                          className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                          title="Edit activity"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                      )}
                      {/* {hasPermission("Activity", "delete") && (
                        <button
                          onClick={() => onDeleteActivity(activity)}
                          className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors"
                          title="Delete activity"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      )} */}
                    </div>
                  </td>

                  {/* Activity Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">{activity.activity_name}</p>
                      </div>
                    </div>
                  </td>

                  {/* Description */}
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">
                      {activity.activity_description ? (
                        <span className="line-clamp-2">{activity.activity_description}</span>
                      ) : (
                        <span className="text-gray-400 italic">No description provided</span>
                      )}
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(activity.status)}</td>

                  {/* Site Completion Deadline — blank or 0 switches the rule off */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {activity.site_completion_deadline_days && activity.site_completion_deadline_days > 0 ? (
                      <span className="text-sm text-gray-900">{activity.site_completion_deadline_days}</span>
                    ) : (
                      <span className="text-sm text-gray-400 italic">No deadline</span>
                    )}
                  </td>

                  {/* Project Count */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <FaChartBar className="w-2 h-2 mr-1" />
                      {activity.project_count || 0}
                    </span>
                  </td>

                  {/* Created At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <span>{formatDate(activity.created_at)}</span>
                    </div>
                  </td>

                  {/* Suspended At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      {activity.suspended_at ? formatDate(activity.suspended_at) : "-"}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination pagination={pagination} onPageChange={onPageChange} onLimitChange={onLimitChange} />

      {/* Excel Download Modal */}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="activities"
        title="Export Activities Data"
        description="Download the filtered activities with full project details"
      />
    </div>
  )
}
